import { Chart as ChartJS } from "chart.js";
import { DEFAULT_DISPLAY_CURRENCY } from "../constants/currency";
import { formatMoney } from "../services/currency";

const LABEL_FONT = "bold 11px 'Crimson Text', serif";
const LABEL_HEIGHT = 14;
const LABEL_PADDING = 4;

const boxesOverlap = (a, b) =>
  a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const makeLabelBox = ({ x, y, width }) => ({
  left: x - width / 2 - LABEL_PADDING,
  right: x + width / 2 + LABEL_PADDING,
  top: y - LABEL_HEIGHT / 2 - LABEL_PADDING,
  bottom: y + LABEL_HEIGHT / 2 + LABEL_PADDING,
});

const getCandidatePositions = ({ chartArea, datapoint, textWidth }) => {
  const halfWidth = textWidth / 2;
  const minX = chartArea.left + halfWidth + LABEL_PADDING;
  const maxX = chartArea.right - halfWidth - LABEL_PADDING;
  const minY = chartArea.top + LABEL_HEIGHT / 2 + LABEL_PADDING;
  const maxY = chartArea.bottom - LABEL_HEIGHT / 2 - LABEL_PADDING;
  const x = clamp(datapoint.x, minX, maxX);

  return [-16, 16, -30, 30].map((offset) => ({
    x,
    y: clamp(datapoint.y + offset, minY, maxY),
  }));
};

const findAvailableLabelPosition = ({
  chartArea,
  datapoint,
  occupiedBoxes,
  textWidth,
}) =>
  getCandidatePositions({ chartArea, datapoint, textWidth }).find((candidate) => {
    const box = makeLabelBox({ ...candidate, width: textWidth });

    return !occupiedBoxes.some((occupied) => boxesOverlap(box, occupied));
  });

/**
 * Chart.js plugin to render currency values on data points.
 */
export const valueLabelPlugin = {
  id: "valueLabelPlugin",
  afterDatasetsDraw(chart) {
    const { ctx, data } = chart;
    const occupiedBoxes = [];
    ctx.save();

    data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (!meta.hidden && meta.data) {
        meta.data.forEach((datapoint, index) => {
          const value = dataset.data[index];
          if (value !== undefined && value !== null) {
            const fmtValue =
              dataset.pointLabels?.[index] ??
              formatMoney(value, dataset.currency || DEFAULT_DISPLAY_CURRENCY, {
                minimumFractionDigits: 0,
              });

            ctx.font = LABEL_FONT;
            const root = document.querySelector(".app-shell") || document.documentElement;
            ctx.fillStyle =
              getComputedStyle(root).getPropertyValue("--app-accent").trim() || "#ce9210";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";

            const textWidth = ctx.measureText(fmtValue).width;
            const position = findAvailableLabelPosition({
              chartArea: chart.chartArea,
              datapoint,
              occupiedBoxes,
              textWidth,
            });

            if (!position) return;

            occupiedBoxes.push(makeLabelBox({ ...position, width: textWidth }));
            ctx.fillText(fmtValue, position.x, position.y);
          }
        });
      }
    });

    ctx.restore();
  },
};

/**
 * Register the value label plugin with Chart.js.
 */
export const registerChartPlugins = () => {
  ChartJS.register(valueLabelPlugin);
};
